import { isEmpty } from 'lodash-es';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import { encode } from 'src/components/conversation-provider/serializer';
import {
  ADF,
  ProviderConfig,
  InlineField,
  PlaceholderApiComment,
  ApiToFabricCommentProps,
} from 'src/components/conversation-provider/types';
import {
  toBBUser,
  apiToFabricComment,
} from 'src/components/conversation-provider/utils';
import { ADD_COMMENT } from 'src/redux/pull-request/actions';
import { BucketDispatch } from 'src/types/state';
import authRequest, { jsonHeaders } from 'src/utils/fetch';
import { short12Hash } from 'src/utils/short-hash';

import { CommentThread } from '../components/comment-tree';
import { uuid } from '../internal/uuid';

/**
 * Adds a comment to a conversation.
 */
export async function addComment(
  dispatch: BucketDispatch,
  user: FabricUser | undefined,
  commentThread: CommentThread,
  conversationId: string,
  parentId: number | null,
  doc: ADF,
  pending?: boolean,
  cslrHash?: string
) {
  // eslint-disable-next-line babel/no-invalid-this
  const { config } = this;
  const { urls, anchor, destRev } = config as ProviderConfig;

  const meta = commentThread.topComment.inline;
  const inline = meta && meta.path ? { path: meta.path } : undefined;
  const timeNow = new Date().toISOString();
  const placeholderId = uuid.generate() as string;

  const placeholderComment: PlaceholderApiComment = {
    created_on: timeNow,
    updated_on: timeNow,
    content: {
      doc,
    },
    user: toBBUser(user),
    placeholderId,
    pending,
  };

  if (parentId) {
    placeholderComment.parent = {
      id: parentId,
    };
  }

  if (meta && !isEmpty(meta)) {
    const inlineField = {} as InlineField;
    if (meta.path) {
      inlineField.path = meta.path;
    }
    if (meta.from) {
      inlineField.from = meta.from;
    }
    if (meta.to) {
      inlineField.to = meta.to;
    }

    inlineField.src_rev = anchor;
    inlineField.dest_rev = destRev;
    inlineField.base_rev = cslrHash;

    placeholderComment.inline = inlineField;
  }

  dispatch({
    type: ADD_COMMENT.REQUEST,
    payload: placeholderComment,
  });

  const bodyJSON: { [key: string]: any } = {
    content: { raw: encode(doc) },
    parent: parentId ? placeholderComment.parent : undefined,
    // inline doesn't include from and to because this will always be for
    // replies to comments or a top-level comment (global) when one already exists
    inline,
    anchor: anchor && short12Hash(anchor),
    dest_rev: destRev && short12Hash(destRev),
  };

  // to avoid including "pending" field for commit comments
  if (pending) {
    bodyJSON.pending = true;
  }

  const response = await fetch(
    authRequest(urls.commentSave(), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(bodyJSON),
    })
  );

  if (!response.ok) {
    throw Error(response.statusText);
  }

  const result = await response.json();
  // Never treat a comment we just created as outdated.
  // (See COREX-1933)
  if (result.inline && result.inline.outdated) {
    result.inline.outdated = false;
  }

  dispatch({
    type: ADD_COMMENT.SUCCESS,
    payload: {
      placeholderId,
      comment: result,
    },
  });

  const apiToFabricCommentProps: ApiToFabricCommentProps = {
    comment: result,
    conversationId,
  };

  return apiToFabricComment(apiToFabricCommentProps);
}
